import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientService, interactionService } from '../services/api';
import PropertyGallery from '@/components/PropertyGallery';
import { Building2, LogOut, MapPin, BedDouble, Bath, Maximize2, Heart, X } from 'lucide-react';

interface ClientInfo {
  id: string;
  agency_id: string;
  email: string;
  name: string;
  phone: string | null;
}

interface Property {
  id: string;
  title: string;
  price: number;
  currency: string;
  address: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  lat: number;
  lng: number;
  images?: string[];
  description?: string;
}

function PropertyCardSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card animate-pulse">
      <div className="h-1 rounded-t-xl bg-muted" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="h-6 bg-muted rounded w-1/3 mt-2" />
        <div className="flex gap-4 mt-3">
          <div className="h-3 bg-muted rounded w-10" />
          <div className="h-3 bg-muted rounded w-10" />
          <div className="h-3 bg-muted rounded w-12" />
        </div>
      </div>
    </div>
  );
}

function ClientPortalPage() {
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [rejected, setRejected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('client_access_token');
    if (!token) {
      navigate('/portal/login');
      return;
    }
    clientService.getMe()
      .then((info) => {
        setClient(info);
        // Restore liked/rejected state from previous sessions (non-fatal)
        interactionService.getActivity(info.id)
          .then((activity) => {
            const likedSet = new Set<string>();
            const rejectedSet = new Set<string>();
            [...activity]
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
              .forEach((ev) => {
                if (ev.type === 'liked') {
                  likedSet.add(ev.property_id);
                  rejectedSet.delete(ev.property_id);
                } else if (ev.type === 'rejected') {
                  rejectedSet.add(ev.property_id);
                  likedSet.delete(ev.property_id);
                } else if (ev.type === 'viewed') {
                  likedSet.delete(ev.property_id);
                  rejectedSet.delete(ev.property_id);
                }
              });
            setLiked(likedSet);
            setRejected(rejectedSet);
          })
          .catch(() => { /* non-fatal */ });
        return clientService.getPortalProperties();
      })
      .then((res) => setProperties(res.items ?? []))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem('client_access_token');
          navigate('/portal/login');
        } else {
          setError('No se pudieron cargar las propiedades.');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    clientService.logout();
  };

  const toggleLike = async (id: string) => {
    const isLiked = liked.has(id);
    setLiked((prev) => {
      const next = new Set(prev);
      isLiked ? next.delete(id) : next.add(id);
      return next;
    });
    if (!isLiked) {
      setRejected((r) => { const rn = new Set(r); rn.delete(id); return rn; });
    }
    try {
      await interactionService.record(id, isLiked ? 'viewed' : 'liked');
    } catch { /* non-fatal */ }
  };

  const toggleReject = async (id: string) => {
    const isRejected = rejected.has(id);
    setRejected((prev) => {
      const next = new Set(prev);
      isRejected ? next.delete(id) : next.add(id);
      return next;
    });
    if (!isRejected) {
      setLiked((l) => { const ln = new Set(l); ln.delete(id); return ln; });
    }
    try {
      await interactionService.record(id, isRejected ? 'viewed' : 'rejected');
    } catch { /* non-fatal */ }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-4xl mx-auto px-6 h-14 flex items-center gap-3">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">Takarah · Portal</span>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="h-6 bg-muted rounded w-48 mb-8 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <PropertyCardSkeleton key={i} />)}
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  const visible = properties.filter((p) => !rejected.has(p.id));

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm">Tu portal · Takarah</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Salir
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-1">
            Bienvenido
          </p>
          <h1 className="text-2xl font-semibold tracking-tight">{client?.name ?? 'Cliente'}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Estas son las propiedades que tu asesor seleccionó para ti
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'Propiedades', value: properties.length },
            { label: 'Favoritas', value: liked.size },
            { label: 'Descartadas', value: rejected.size },
          ].map(({ label, value }) => (
            <div key={label} className="border border-border rounded-xl px-4 py-3">
              <p className="text-xl font-semibold">{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium">Aún no hay propiedades</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Tu asesor aún no ha compartido propiedades contigo. Vuelve pronto.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visible.map((p) => (
              <div
                key={p.id}
                className="rounded-xl border border-border bg-card overflow-hidden group"
              >
                <div
                  className="h-1"
                  style={{ background: liked.has(p.id) ? 'hsl(var(--primary))' : 'transparent' }}
                />
                {p.images && p.images.length > 0 && (
                  <div className="p-3 pb-0">
                    <PropertyGallery images={p.images} />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-semibold leading-tight line-clamp-2">{p.title}</h3>
                  {p.address && (
                    <div className="flex items-center gap-1 mt-1.5 text-muted-foreground">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="text-xs truncate">{p.address}</span>
                    </div>
                  )}
                  <p className="text-xl font-semibold text-primary mt-3">
                    ${p.price.toLocaleString('es-MX')}
                  </p>
                  {p.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1 mb-2">
                      {p.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-muted-foreground text-xs">
                    {p.bedrooms != null && (
                      <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{p.bedrooms}</span>
                    )}
                    {p.bathrooms != null && (
                      <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.bathrooms}</span>
                    )}
                    {p.area != null && (
                      <span className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" />{p.area} m²</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                    <button
                      onClick={() => toggleLike(p.id)}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                        liked.has(p.id)
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${liked.has(p.id) ? 'fill-primary' : ''}`} />
                      {liked.has(p.id) ? 'Favorita' : 'Me interesa'}
                    </button>
                    <button
                      onClick={() => toggleReject(p.id)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Descartar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-border mt-12 py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="w-3.5 h-3.5" />
          <span>Portal de cliente · Takarah · Solo lectura</span>
        </div>
      </footer>
    </div>
  );
}

export default ClientPortalPage;
